import axios from "axios";
import _ from "lodash";
import trim from "lodash/trim";

import { FORM_TYPES } from "../../../evidence/actions/ActionOthers/actionOther.constants";

import { retrieveToken } from "../../../shared/api/client";
import { JwtService } from "../../../shared/services/Jwt.service";
import LocalStorage from "../../../shared/services/LocalStorage.service";
import UiCache from "../../../shared/services/UiCache.service";

const IMPERS_LS_KEY = "impersonationStateFrom";
const AUTH_URL = `${process.env.GATEWAY_URL}/authenticate`;
const SWITCH_AUTH_URL = `${process.env.GATEWAY_URL}/switch-authentication`;

function clearAccessToken() {
  JwtService.removeToken();
  LocalStorage.removeFromLocalStorage("user");
}

async function clearRefreshToken() {
  return axios.delete(AUTH_URL);
}

function clearUserCache() {
  UiCache.deleteParams();

  const farms = LocalStorage.loadFromLocalStorage("farms");
  if (farms && Array.isArray(farms)) {
    farms.forEach((farm) => {
      LocalStorage.removeFromLocalStorage(`state_eph_${farm.id}`);
      LocalStorage.removeFromLocalStorage(`state_vrf_${farm.id}`);
      LocalStorage.removeFromLocalStorage(`state_vrs_${farm.id}`);
      Object.keys(FORM_TYPES).forEach((formType) =>
        LocalStorage.removeFromLocalStorage(`form_state_${formType}${farm.id}`),
      );
    });
  }
  LocalStorage.removeFromLocalStorage("farms");
}

const logout = async () => {
  clearAccessToken();
  await clearRefreshToken();
  clearUserCache();
  window.location.assign("/login");
};

function impersonate(farm, userId, location) {
  return handleTokenResponse(doSwitchAuthPost(userId))
    .then((token) => setupUser(token))
    .then(() => {
      LocalStorage.removeFromLocalStorage(IMPERS_LS_KEY);

      window.location.assign(
        location ??
          window.location.pathname.split("/").with(2, farm.id).join("/"),
      );
    });
}

function login(credentials) {
  const data = {
    username: trim(credentials.username),
    password: trim(credentials.password),
    rememberMe: true, // this should be set from FE later on
  };

  return handleTokenResponse(doLoginPost(data)).then((token) =>
    setupUser(token),
  );
}

function doLoginPost(credentials) {
  const headers = {
    "Content-Type": "application/json",
  };

  return axios.post(AUTH_URL, credentials, {
    headers,
    withCredentials: true,
  });
}

function handleTokenResponse(promise) {
  return promise
    .then((response) => {
      const token = response.data;
      JwtService.storeToken(token);
      return token;
    })
    .catch((e) => {
      throw _.pick(e.response.data, [
        "status",
        "error",
        "message",
        "path",
        "timestamp",
      ]);
    });
}

function doSwitchAuthPost(userId) {
  const headers = {
    "Content-Type": "application/json",
    Authorization: getAuthHeader(),
  };

  return axios.post(SWITCH_AUTH_URL, userId, {
    headers,
    withCredentials: userId !== null,
  });
}

function getAuthHeader() {
  return `Bearer ${JwtService.getTokenValue()}`;
}

function setupUser(token) {
  return setUser(token.principal.toLowerCase());
}

async function setUser(username) {
  return axios
    .get(`${process.env.GATEWAY_URL}/users/${username}`)
    .then((userData) => {
      LocalStorage.saveToLocalStorage(userData.data, "user");
      return userData.data;
    });
}

function deimpersonate() {
  return handleTokenResponse(doSwitchAuthPost(null))
    .then((token) => setupUser(token))
    .then(() => {
      const token = JwtService.readToken();
      const farm = token.user_farms.toSorted(
        ({ name: aName }, { name: bName }) =>
          aName && bName ? aName.localeCompare(bName) : 0,
      );
      LocalStorage.removeFromLocalStorage(IMPERS_LS_KEY);

      if (window.location.pathname.includes("sensors")) {
        redirectToSensors(farm);
      } else {
        redirectToFarm(farm);
      }
    });
}

function isImpersonated() {
  return JwtService.isImpersonatedToken();
}

const redirectToSensors = (farm) => {
  const newLocation = [
    ...window.location.pathname.split("/").with(2, farm).slice(0, 3),
    "admin",
    "sensors",
  ].join("/");

  window.location.assign(newLocation);
};

const redirectToFarm = (farm) => {
  const newLocation = window.location.pathname
    .split("/")
    .with(2, farm)
    .join("/");

  window.location.assign(newLocation);
};

const clearAllUserData = () => {
  clearAccessToken();
  clearRefreshToken();
  clearUserCache();
};

const refreshLogin = () => {
  const shouldUpdateUser = !LocalStorage.loadFromLocalStorage("user");
  let respPromise = handleTokenResponse(retrieveToken());

  if (shouldUpdateUser) {
    respPromise = respPromise.then((token) => setupUser(token));
  }

  return respPromise
    .then(() => retrieveToken())
    .catch(() => {
      logout();
    });
};

export {
  login,
  logout,
  impersonate,
  deimpersonate,
  isImpersonated,
  clearAllUserData,
  refreshLogin,
};
